import React, { useState, useRef } from 'react';

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  background: 'var(--surface-1)',
  border: '1px solid var(--card-border)',
  borderRadius: '8px',
  color: 'var(--text-primary)',
  fontSize: '13px',
  outline: 'none',
  transition: 'border-color 0.15s ease',
  boxSizing: 'border-box',
};

const tagStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '3px 10px',
  background: 'var(--brand-light)',
  border: '1px solid var(--brand-border)',
  borderRadius: '20px',
  color: 'var(--brand)',
  fontSize: '12px',
  fontWeight: 600,
};

const iconBtnStyle = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  borderRadius: '6px',
  fontSize: '12px',
  color: 'var(--text-muted)',
  display: 'flex',
  alignItems: 'center',
  transition: 'background 0.15s, color 0.15s',
};

/**
 * CategoryCard — represents a single named skill category.
 * Allows renaming the category, adding skills (via Enter/comma), and deleting skills/category.
 */
export default function CategoryCard({ category, onRename, onDelete, onAddSkill, onDeleteSkill }) {
  const [inputVal, setInputVal] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(category.name);
  const inputRef = useRef(null);

  const commitAdd = () => {
    const trimmed = inputVal.trim().replace(/,+$/, '');
    if (trimmed) onAddSkill(category.id, trimmed);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitAdd();
    }
  };

  const commitRename = () => {
    const trimmed = nameVal.trim();
    if (trimmed && trimmed !== category.name) onRename(category.id, trimmed);
    setEditingName(false);
  };

  return (
    <div style={{
      background: 'var(--surface-1)',
      border: '1px solid var(--card-border)',
      borderRadius: '10px',
      overflow: 'hidden',
    }}>
      {/* ── Card header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        background: 'var(--surface-2, rgba(255,255,255,0.03))',
        borderBottom: '1px solid var(--card-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <span style={{ fontSize: '13px', opacity: 0.5 }}>⋮⋮</span>
          {editingName ? (
            <input
              autoFocus
              value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setNameVal(category.name); setEditingName(false); }
              }}
              style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px', fontWeight: 700, width: 'auto', flex: 1, maxWidth: '180px' }}
            />
          ) : (
            <span
              style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)', letterSpacing: '0.3px', cursor: 'pointer' }}
              title="Click to rename"
              onClick={() => { setNameVal(category.name); setEditingName(true); }}
            >
              {category.name}
            </span>
          )}
          <span style={{
            fontSize: '10px',
            background: 'var(--brand-light)',
            color: 'var(--brand)',
            padding: '1px 6px',
            borderRadius: '10px',
            fontWeight: 700,
          }}>
            {category.skills.length}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          <button
            style={iconBtnStyle}
            title="Rename category"
            onClick={() => { setNameVal(category.name); setEditingName(true); }}
          >✏️</button>
          <button
            style={{ ...iconBtnStyle, color: 'var(--error, #ef4444)' }}
            title="Delete category"
            onClick={() => onDelete(category.id)}
          >🗑️</button>
        </div>
      </div>

      {/* ── Card body ── */}
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputVal.trim()) commitAdd(); }}
          placeholder={`Add skill to ${category.name}… (Enter or comma)`}
          style={{ ...inputStyle, fontSize: '12px' }}
        />

        {category.skills.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {category.skills.map((skill) => (
              <div key={skill} style={tagStyle}>
                <span>{skill}</span>
                <button
                  onClick={() => onDeleteSkill(category.id, skill)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '0 2px',
                    fontSize: '11px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title="Remove skill"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            No skills yet — type above to add.
          </span>
        )}
      </div>
    </div>
  );
}
